import { useState } from 'react'
import { Head, router, useForm } from '@inertiajs/react'
import Layout from '../components/Layout'
import Avatar from '../components/Avatar'

function MemberForm({ member, onDone }) {
  const editing = Boolean(member)
  const { data, setData, post, patch, processing, errors, reset } = useForm({
    username: member?.username || '',
    email: member?.email || '',
    name: member?.name || '',
    relation: member?.relation || '',
    role: member?.role || 'family',
    password: '',
    password_confirmation: '',
  })

  function submit(event) {
    event.preventDefault()
    const options = {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        onDone()
      },
    }

    if (editing) {
      patch(`/members/${member.id}`, options)
    } else {
      post('/members', options)
    }
  }

  return (
    <form onSubmit={submit} className="card-cute animate-pop-in space-y-4 p-6">
      <h2 className="font-display text-2xl font-extrabold text-ink">
        {editing ? `Edit ${member.name}` : 'Invite a friend or family member 💌'}
      </h2>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-1.5 block font-display font-bold text-ink-soft">
            Name
          </label>
          <input
            id="name"
            type="text"
            value={data.name}
            onChange={(event) => setData('name', event.target.value)}
            required
            placeholder="Jo Campbell"
            className="input-cute"
          />
          {errors.name && <p className="mt-1 text-sm font-bold text-rose-600">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="username" className="mb-1.5 block font-display font-bold text-ink-soft">
            Username
          </label>
          <input
            id="username"
            type="text"
            value={data.username}
            onChange={(event) => setData('username', event.target.value)}
            required
            autoCapitalize="none"
            spellCheck="false"
            placeholder="grandma_jo"
            className="input-cute"
          />
          {errors.username && <p className="mt-1 text-sm font-bold text-rose-600">{errors.username}</p>}
        </div>

        <div>
          <label htmlFor="email" className="mb-1.5 block font-display font-bold text-ink-soft">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={data.email}
            onChange={(event) => setData('email', event.target.value)}
            required
            autoCapitalize="none"
            spellCheck="false"
            placeholder="jo@example.com"
            className="input-cute"
          />
          {errors.email && <p className="mt-1 text-sm font-bold text-rose-600">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="relation" className="mb-1.5 block font-display font-bold text-ink-soft">
            Relation <span className="font-normal">(optional)</span>
          </label>
          <input
            id="relation"
            type="text"
            value={data.relation}
            onChange={(event) => setData('relation', event.target.value)}
            placeholder="Grandma"
            className="input-cute"
          />
        </div>

        <div>
          <label htmlFor="role" className="mb-1.5 block font-display font-bold text-ink-soft">
            Role
          </label>
          <select
            id="role"
            value={data.role}
            onChange={(event) => setData('role', event.target.value)}
            className="input-cute cursor-pointer"
          >
            <option value="family">Friends &amp; family — can view, add and heart photos</option>
            <option value="admin">Admin — can also manage the friends and family list</option>
          </select>
        </div>

        <div>
          <label htmlFor="password" className="mb-1.5 block font-display font-bold text-ink-soft">
            {editing ? 'New password (leave blank to keep)' : 'Password'}
          </label>
          <input
            id="password"
            type="password"
            value={data.password}
            onChange={(event) => setData('password', event.target.value)}
            required={!editing}
            maxLength={72}
            autoComplete="new-password"
            placeholder="At least 8 characters"
            className="input-cute"
          />
          {errors.password && <p className="mt-1 text-sm font-bold text-rose-600">{errors.password}</p>}
        </div>

        <div>
          <label htmlFor="password_confirmation" className="mb-1.5 block font-display font-bold text-ink-soft">
            Confirm password
          </label>
          <input
            id="password_confirmation"
            type="password"
            value={data.password_confirmation}
            onChange={(event) => setData('password_confirmation', event.target.value)}
            required={!editing}
            maxLength={72}
            autoComplete="new-password"
            className="input-cute"
          />
          {errors.password_confirmation && (
            <p className="mt-1 text-sm font-bold text-rose-600">{errors.password_confirmation}</p>
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <button type="submit" disabled={processing} className="btn-cute bg-blush-400 text-white shadow hover:bg-blush-500">
          {editing ? 'Save changes' : 'Create account'}
        </button>
        <button type="button" onClick={onDone} className="btn-cute border-2 border-blush-100 bg-white text-ink-soft">
          Cancel
        </button>
      </div>
    </form>
  )
}

function PendingRequests({ requests }) {
  function approve(request) {
    router.post(`/members/${request.id}/approve`, {}, { preserveScroll: true })
  }

  function decline(request) {
    if (!confirm(`Turn down ${request.name}'s request?`)) return
    router.delete(`/members/${request.id}`, { preserveScroll: true })
  }

  return (
    <section className="mb-8">
      <h2 className="mb-3 font-display text-2xl font-extrabold text-ink">
        Waiting to be let in
        <span className="ml-2 rounded-full bg-blush-400 px-2.5 py-0.5 align-middle text-sm text-white">
          {requests.length}
        </span>
      </h2>

      <div className="grid gap-4 sm:grid-cols-2">
        {requests.map((request) => (
          <div key={request.id} className="card-cute animate-pop-in flex flex-col gap-4 border-2 border-blush-100 p-5">
            <div className="flex items-center gap-4">
              <Avatar initials={request.initials} size="lg" />

              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-lg font-extrabold text-ink">{request.name}</p>
                <p className="truncate text-sm text-ink-soft">
                  @{request.username}
                  {request.relation ? ` · ${request.relation}` : ''}
                </p>
                <p className="truncate text-sm text-ink-soft/80">{request.email}</p>
                <p className="mt-1 text-xs font-bold text-ink-soft/70">Asked on {request.requestedAt}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => approve(request)}
                className="btn-cute flex-1 bg-mint-baby text-emerald-900 shadow hover:brightness-95"
              >
                ✓ Let them in
              </button>
              <button
                type="button"
                onClick={() => decline(request)}
                className="btn-cute border-2 border-blush-100 bg-white text-ink-soft hover:bg-rose-50"
              >
                Not now
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default function Members({ members, pendingRequests }) {
  const [showForm, setShowForm] = useState(false)
  const [editingMember, setEditingMember] = useState(null)

  function remove(member) {
    if (!confirm(`Remove ${member.name}? Their photos are removed too.`)) return
    router.delete(`/members/${member.id}`, { preserveScroll: true })
  }

  function closeForms() {
    setShowForm(false)
    setEditingMember(null)
  }

  return (
    <Layout>
      <Head title="Friends &amp; family" />

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-ink sm:text-4xl">Friends &amp; family 👨‍👩‍👧‍👦</h1>
          <p className="mt-1 text-ink-soft sm:mt-2 sm:text-lg">
            Everyone who can see the album — plus anyone waiting on your say-so.
          </p>
        </div>

        {!showForm && !editingMember && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="btn-cute bg-blush-400 text-white shadow-lg hover:bg-blush-500"
          >
            ＋ Add someone
          </button>
        )}
      </div>

      {(showForm || editingMember) && (
        <div className="mb-8">
          <MemberForm key={editingMember?.id || 'new'} member={editingMember} onDone={closeForms} />
        </div>
      )}

      {pendingRequests.length > 0 && <PendingRequests requests={pendingRequests} />}

      <div className="grid gap-4 sm:grid-cols-2">
        {members.map((member) => (
          <div key={member.id} className="card-cute flex items-center gap-4 p-5">
            <Avatar initials={member.initials} size="lg" />

            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-lg font-extrabold text-ink">
                {member.name}
                {member.isYou && <span className="ml-2 text-sm font-bold text-blush-500">(you)</span>}
              </p>
              <p className="truncate text-sm text-ink-soft">
                @{member.username}
                {member.relation ? ` · ${member.relation}` : ''}
              </p>
              <p className="truncate text-sm text-ink-soft/80">{member.email}</p>
              <p className="mt-1 flex flex-wrap gap-2 text-xs font-bold">
                <span
                  className={`rounded-full px-2 py-0.5 ${
                    member.role === 'admin' ? 'bg-lilac-baby text-indigo-800' : 'bg-mint-baby text-emerald-800'
                  }`}
                >
                  {member.role === 'admin' ? 'Admin' : 'Friends & family'}
                </span>
                <span className="rounded-full bg-white px-2 py-0.5 text-ink-soft">
                  {member.photoCount} {member.photoCount === 1 ? 'photo' : 'photos'}
                </span>
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false)
                  setEditingMember(member)
                }}
                aria-label={`Edit ${member.name}`}
                className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full transition hover:bg-blush-50 sm:h-9 sm:w-9"
              >
                ✏️
              </button>
              {!member.isYou && (
                <button
                  type="button"
                  onClick={() => remove(member)}
                  aria-label={`Remove ${member.name}`}
                  className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full transition hover:bg-rose-50 sm:h-9 sm:w-9"
                >
                  🗑️
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Layout>
  )
}
